import { Loader2, Mail, Plus, Upload, X, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { EMAIL_EXTENSIONS, SectionLabel } from "./reviewMeta";

const inputCls =
  "w-full bg-white border border-stone-300 rounded-lg px-4 py-3 text-base text-slate-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 transition-all disabled:opacity-60";

/**
 * Step 1: the evidence pack. Proposal number, client draft, and the
 * correspondence (files or pasted text). Submitting runs the
 * pre-flight check, not the review.
 */
export default function EvidenceForm({ state, setState, busy, onSubmit }) {
  const { entryNumber, file, emailFiles, emailText } = state;
  const patch = (p) => setState((s) => ({ ...s, ...p }));

  const onAddEmails = (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!chosen.length) return;
    const bad = chosen.find((f) => !EMAIL_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext)));
    if (bad) {
      toast.error(`${bad.name}: email files must be .msg, .eml, .pdf or .txt.`);
      return;
    }
    const next = [...emailFiles, ...chosen];
    if (next.length > 10) toast.error("Maximum 10 email files per review.");
    patch({ emailFiles: next.slice(0, 10) });
  };

  const removeEmail = (i) => patch({ emailFiles: emailFiles.filter((_, idx) => idx !== i) });

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm"
      data-testid="contract-review-form"
    >
      <div className="flex items-center gap-2 mb-5">
        <span className="w-6 h-6 rounded-full bg-[#1B4F8A] text-white text-[11px] font-mono flex items-center justify-center">1</span>
        <p className="text-sm font-medium text-slate-900">Assemble the evidence pack</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <label className="block">
          <SectionLabel>Proposal number</SectionLabel>
          <input
            type="number"
            min="1"
            required
            value={entryNumber}
            onChange={(e) => patch({ entryNumber: e.target.value })}
            placeholder="e.g. 6912"
            data-testid="contract-review-entry-input"
            disabled={busy}
            className={`${inputCls} font-mono`}
          />
        </label>
        <label className="block">
          <SectionLabel>Client draft contract (.docx or .pdf, max 25 MB)</SectionLabel>
          <label className="flex items-center justify-center gap-2 bg-stone-50 border border-dashed border-stone-300 hover:border-[#3EA8DC] rounded-lg px-4 py-3 cursor-pointer transition-colors">
            <Upload className="w-4 h-4 text-stone-500" />
            <span className="text-sm text-slate-700 truncate" data-testid="contract-review-file-name">
              {file ? file.name : "Choose file…"}
            </span>
            <input
              type="file"
              accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => patch({ file: e.target.files?.[0] || null })}
              disabled={busy}
              data-testid="contract-review-file-input"
              className="hidden"
            />
          </label>
        </label>
      </div>

      <div className="mt-6">
        <SectionLabel>Email correspondence with the client (required)</SectionLabel>
        <div className="flex flex-wrap items-center gap-2" data-testid="contract-review-email-list">
          {emailFiles.map((f, i) => (
            <span
              key={`${f.name}-${i}`}
              data-testid={`contract-review-email-chip-${i}`}
              className="inline-flex items-center gap-1.5 bg-[#3EA8DC]/10 border border-[#3EA8DC]/30 text-slate-700 text-xs rounded-full pl-3 pr-1.5 py-1.5"
            >
              <Mail className="w-3 h-3 text-[#1B4F8A] shrink-0" />
              <span className="max-w-[220px] truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => removeEmail(i)}
                disabled={busy}
                aria-label={`Remove ${f.name}`}
                data-testid={`contract-review-email-remove-${i}`}
                className="text-stone-400 hover:text-rose-500 transition-colors p-0.5"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          <label className="inline-flex items-center gap-2 bg-stone-50 border border-dashed border-stone-300 hover:border-[#3EA8DC] rounded-full px-4 py-2 cursor-pointer transition-colors">
            <Plus className="w-3.5 h-3.5 text-stone-500" />
            <span className="text-xs text-slate-700">
              {emailFiles.length ? "Add another thread…" : "Add email thread(s)…"}
            </span>
            <input
              type="file"
              accept=".msg,.eml,.pdf,.txt"
              multiple
              onChange={onAddEmails}
              disabled={busy}
              data-testid="contract-review-email-input"
              className="hidden"
            />
          </label>
        </div>
        <p className="text-[11px] text-stone-500 mt-2 italic max-w-3xl">
          Outlook .msg or .eml, a PDF printout of the chain, or a .txt export. Upload the latest message of each
          thread, it carries the quoted history. Documents attached inside the emails (DOCX, PDF, TXT) are read
          as part of the correspondence.
        </p>
        <details className="mt-3 group">
          <summary className="cursor-pointer text-xs text-[#1B4F8A] hover:underline inline-flex items-center gap-1.5 select-none">
            <ClipboardList className="w-3.5 h-3.5" />
            Or paste email text
          </summary>
          <textarea
            value={emailText}
            onChange={(e) => patch({ emailText: e.target.value })}
            disabled={busy}
            rows={6}
            placeholder="Paste the email thread here (From / Sent / Subject lines help the reviewer date each message)."
            data-testid="contract-review-email-text"
            className={`${inputCls} mt-2 text-sm resize-y`}
          />
        </details>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-stone-500 italic max-w-md">
          Nothing is analysed yet. The next step shows you exactly which four sources the review will use and
          flags anything that does not line up.
        </p>
        <button
          type="submit"
          disabled={busy || !file || !entryNumber}
          data-testid="contract-review-submit-btn"
          className="bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg transition-colors inline-flex items-center gap-2 shadow-md shadow-[#1B4F8A]/20"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
          {busy ? "Checking…" : "Check evidence pack"}
        </button>
      </div>
    </form>
  );
}
